// The editor's side of a buddy session (docs/COLLABORATION_DESIGN.md): each
// shared file is a Y.Text, and each of its documents in the editor follows
// it. The editor keeps one document for each open file and shows one at a
// time, so the session applies the room's changes to whichever documents
// the file has, shown or not, and passes on the changes typed here.
//
// Changes from the room are kept out of the undo history, which maps the
// person's own edits through them: Ctrl+Z undoes your own typing, never
// someone else's.

import { StateField, StateEffect, Annotation } from "@codemirror/state";
import { Decoration, WidgetType, EditorView } from "@codemirror/view";

// Marks a transaction that brings in the room's changes.
export const fromRoom = Annotation.define();

// A Y.Text event's delta as CodeMirror changes, all from the document as it
// was before them.
export function deltaChanges(delta) {
  const changes = [];
  let position = 0;
  for (const part of delta) {
    if (part.insert != null) {
      changes.push({ from: position, insert: typeof part.insert === "string" ? part.insert : "" });
    } else if (part.delete != null) {
      changes.push({ from: position, to: position + part.delete });
      position += part.delete;
    } else {
      position += part.retain;
    }
  }
  return changes;
}

// Passes a CodeMirror ChangeSet on to a Y.Text, whose text it was made from.
export function applyChanges(ytext, changes) {
  let shift = 0;
  changes.iterChanges((fromA, toA, fromB, toB, inserted) => {
    const text = inserted.sliceString(0, inserted.length, "\n");
    if (toA > fromA) ytext.delete(fromA + shift, toA - fromA);
    if (text) ytext.insert(fromA + shift, text);
    shift += text.length - (toA - fromA);
  });
}

// Replaces a Y.Text's text with another, changing only the stretch between
// the parts they share at the start and the end, so that others' cursors
// elsewhere stay where they are.
export function replaceText(ytext, text) {
  const old = ytext.toString();
  if (old === text) return;
  let start = 0;
  while (start < old.length && start < text.length && old[start] === text[start]) ++start;
  let end = 0;
  while (end < old.length - start && end < text.length - start &&
    old[old.length - 1 - end] === text[text.length - 1 - end]) ++end;
  if (old.length - start - end > 0) ytext.delete(start, old.length - start - end);
  if (text.length - start - end > 0) ytext.insert(start, text.slice(start, text.length - end));
}

// Other people's cursors and selections in the file shown: [{ id, from, to,
// head, colour, name, recent }].
export const setCursors = StateEffect.define();

class CursorWidget extends WidgetType {
  constructor(cursor) {
    super();
    this.cursor = cursor;
  }
  eq(other) {
    const a = this.cursor, b = other.cursor;
    return a.id === b.id && a.colour === b.colour && a.name === b.name && a.recent === b.recent;
  }
  toDOM() {
    const caret = document.createElement("span");
    caret.className = "buddy-caret" + (this.cursor.recent ? " recent" : "");
    caret.style.setProperty("--buddy", this.cursor.colour);
    const name = document.createElement("span");
    name.className = "buddy-name";
    name.textContent = this.cursor.name;
    caret.append(name);
    caret.title = this.cursor.name;
    return caret;
  }
  ignoreEvent() { return true; }
}

export const cursorField = StateField.define({
  create: () => Decoration.none,
  update(decorations, transaction) {
    decorations = decorations.map(transaction.changes);
    for (const effect of transaction.effects) {
      if (!effect.is(setCursors)) continue;
      const length = transaction.state.doc.length;
      const ranges = [];
      for (const cursor of effect.value) {
        const from = Math.max(0, Math.min(cursor.from, length)), to = Math.max(0, Math.min(cursor.to, length));
        const head = Math.max(0, Math.min(cursor.head, length));
        if (to > from) {
          ranges.push(Decoration.mark({ class: "buddy-selection", attributes: { style: `--buddy: ${cursor.colour}` } })
            .range(from, to));
        }
        ranges.push(Decoration.widget({ widget: new CursorWidget(cursor), side: 1 }).range(head));
      }
      decorations = Decoration.set(ranges, true);
    }
    return decorations;
  },
  provide: (field) => EditorView.decorations.from(field),
});
